import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Edit2, Trash2, Bell, ChevronRight, Search } from "lucide-react";
import { CONTENT } from "@/constants/testIds";
import { useDepartments } from "@/hooks/useDepartments";

import { API } from "../lib/api";
const ax = () => axios.create({ withCredentials: true });

function formatDate(dt) { try { return new Date(dt).toLocaleDateString("pt-BR"); } catch { return dt; } }

const priorityStyles = {
  high: { label: "Alta Prioridade", dot: "bg-red-500", bg: "border-l-red-400" },
  normal: { label: "Normal", dot: "bg-blue-400", bg: "border-l-blue-300" },
  low: { label: "Baixa", dot: "bg-gray-400", bg: "border-l-gray-300" }
};

export default function AnnouncementsPage() {
  const { hasAnyRole } = useAuth();
  const canEdit = hasAnyRole(["autor_area", "administrador_total", "admin"]);
  const { departments } = useDepartments();
  const DEPTS = departments;
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", content: "", department: "", priority: "normal" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => ax().get(`${API}/announcements`).then(r => setItems(r.data)).catch(console.error).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const filtered = items.filter(i =>
    i.title?.toLowerCase().includes(search.toLowerCase()) ||
    i.content?.toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => { setEditing(null); setForm({ title: "", content: "", department: "", priority: "normal" }); setShowForm(true); };
  const openEdit = (item) => { setEditing(item); setForm({ title: item.title, content: item.content, department: item.department || "", priority: item.priority || "normal" }); setShowForm(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await ax().put(`${API}/announcements/${editing.item_id}`, form);
      else await ax().post(`${API}/announcements`, form);
      setShowForm(false); load();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este aviso?")) return;
    await ax().delete(`${API}/announcements/${id}`);
    load(); if (selected?.item_id === id) setSelected(null);
  };

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-amber-500" />
          <div>
            <h1 className="text-xl font-bold font-heading text-slate-900">Mural de Avisos</h1>
            <p className="text-sm text-slate-500">{items.filter(i => i.is_active).length} avisos ativos</p>
          </div>
        </div>
        {canEdit && (
          <Button data-testid={CONTENT.createBtn} onClick={openCreate} style={{ backgroundColor: "#2346ff" }} className="text-white gap-2">
            <Plus className="w-4 h-4" /> Novo Aviso
          </Button>
        )}
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input placeholder="Buscar avisos..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-20 bg-slate-100 rounded-xl animate-pulse" />)}</div>
      ) : (
        <div data-testid={CONTENT.contentList} className="space-y-3">
          {filtered.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              <Bell className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Nenhum aviso encontrado</p>
            </div>
          )}
          {filtered.map(item => {
            const pStyle = priorityStyles[item.priority] || priorityStyles.normal;
            return (
              <div key={item.item_id} data-testid={CONTENT.contentItem}
                className={`bento-card p-4 cursor-pointer group border-l-4 ${pStyle.bg}`}
                onClick={() => setSelected(selected?.item_id === item.item_id ? null : item)}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 mt-2 ${pStyle.dot}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-slate-800 group-hover:text-[#2346ff] transition-colors text-sm">{item.title}</h3>
                        <Badge className={`text-xs ${item.priority === "high" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}>{pStyle.label}</Badge>
                        {item.department && <Badge variant="secondary" className="text-xs">{item.department}</Badge>}
                      </div>
                      <p className="text-xs text-slate-500 mt-1 line-clamp-2">{item.content}</p>
                      <p className="text-xs text-slate-400 mt-1">{formatDate(item.created_at)} · {item.created_by_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${selected?.item_id === item.item_id ? "rotate-90" : ""}`} />
                    {canEdit && (
                      <>
                        <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100" onClick={e => { e.stopPropagation(); openEdit(item); }}><Edit2 className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 text-red-500" onClick={e => { e.stopPropagation(); remove(item.item_id); }}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </>
                    )}
                  </div>
                </div>
                {selected?.item_id === item.item_id && (
                  <div className="mt-3 pt-3 border-t border-slate-100 ml-5">
                    <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{item.content}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar Aviso" : "Novo Aviso"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Título *</Label><Input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} /></div>
            <div><Label>Mensagem *</Label><Textarea rows={4} value={form.content} onChange={e => setForm(f => ({...f, content: e.target.value}))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Prioridade</Label>
                <Select value={form.priority} onValueChange={v => setForm(f => ({...f, priority: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">Alta</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="low">Baixa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Departamento</Label>
                <Select value={form.department || "__none"} onValueChange={v => setForm(f => ({...f, department: v === "__none" ? "" : v}))}>
                  <SelectTrigger><SelectValue placeholder="Geral" /></SelectTrigger>
                  <SelectContent><SelectItem value="__none">Geral (todos)</SelectItem>{DEPTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving || !form.title || !form.content} style={{ backgroundColor: "#2346ff" }} className="text-white">{saving ? "Salvando..." : "Publicar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
